/* eslint-disable react-hooks/exhaustive-deps */
import { db } from "@/firebase";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { useCallback, useContext, useState } from "react";
import { reduceQuantity, increaseQuantity, addItemsToOrder, removeItemFromOrder, createOrder, updateOrder } from "../features/order/slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { useArchiveOrderMutation } from "@/features/organizations/api/ordersApi";
import { localStorage as localStorageType } from "@/constants/localStorage";
import { Item, Order, OrderItem, OrderStatus } from "@/types";

const useOrder = () => {
  const dispatch = useAppDispatch();
  const order = useAppSelector((state: RootState) => state.order);
  const { organization } = useContext(WebsiteContext);
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const [archiveOrder] = useArchiveOrderMutation();
  const [orders, setOrders] = useState<Order[]>([]);

  const orgId = organization ? organization.id : organizationId;

  const isIncluded = useCallback((id: string) => {
    return !!order.items.find((item: Item) => item.id === id);
  }, []);

  const increaseItemQuantity = useCallback((item: OrderItem) => {
    dispatch(increaseQuantity(item.id as unknown as OrderItem));
  }, []);

  const reduceItemQuantity = useCallback((id: string) => {
    dispatch(reduceQuantity(id));
  }, []);

  const checkOrderInLocalStorage = useCallback((organizationId: string) => {
    if (!organizationId) return;

    const lsOrganizationOrders = localStorage.getItem(localStorageType.ORGANIZATION_ORDERS) ?? "{}";
    const organizationOrders = JSON.parse(lsOrganizationOrders);
    return organizationOrders[organizationId];
  }, []);

  const deleteLSOrganizationOrder = useCallback(() => {
    if (!orgId) return;

    const lsOrganizationOrders = localStorage.getItem(localStorageType.ORGANIZATION_ORDERS) ?? "{}";
    const organizationOrders = JSON.parse(lsOrganizationOrders);
    delete organizationOrders[orgId];
    localStorage.setItem(localStorageType.ORGANIZATION_ORDERS, JSON.stringify(organizationOrders));
  }, []);

  const subscribeToOrderByOrganizationId = useCallback(
    (organizationId: string) => {
      const orderId = checkOrderInLocalStorage(organizationId);
      const docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);

      return onSnapshot(
        docRef,
        (doc) => {
          if (doc.exists()) {
            const order = { ...doc.data(), id: doc.id } as Order;

            dispatch(updateOrder(order as Order));

            if ([OrderStatus.DELIVERED, OrderStatus.CANCELLED, OrderStatus.REJECTED].includes(order.status)) {
              deleteLSOrganizationOrder();
            }
          } else {
            deleteLSOrganizationOrder();
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    [dispatch]
  );

  const subscribeToOrdersByOrganizationId = useCallback(
    (organizationId: string) => {
      const colRef = collection(db, `organizations/${organizationId}/orders`);

      return onSnapshot(
        colRef,
        (snapshot) => {
          const orders: Order[] = [];

          snapshot.forEach((doc) => {
            orders.push({ ...doc.data(), id: doc.id } as Order);
          });

          setOrders(orders);
        },
        (error) => {
          console.error(error);
        }
      );
    },
    [dispatch]
  );

  const getOrderInLocalStorage = useCallback((organizationId: string) => {
    if (!organizationId) return;
    return checkOrderInLocalStorage(organizationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrderInLocalStorage = useCallback((organizationId?: string, orderId?: string) => {
    if (!organizationId || !orderId) return;

    const lsOrganizationOrders = localStorage.getItem(localStorageType.ORGANIZATION_ORDERS) ?? "{}";
    const organizationOrders = JSON.parse(lsOrganizationOrders);

    organizationOrders[organizationId] = orderId;

    localStorage.setItem(localStorageType.ORGANIZATION_ORDERS, JSON.stringify(organizationOrders));
  }, []);

  const addItems = useCallback(async (items: OrderItem[]) => {
    if (!items) return;
    const isOrder = "createdAt" in order;

    if (!isOrder && orgId) {
      const newOrder = await dispatch(createOrder({ organizationId: orgId, id: order?.id }));
      saveOrderInLocalStorage(orgId, newOrder.payload?.id);
    } else {
      saveOrderInLocalStorage(orgId, order?.id);
    }

    dispatch(addItemsToOrder({ items, organizationId: orgId }));
  }, []);

  const removeItem = useCallback((item: OrderItem) => {
    if (!item) return;
    dispatch(removeItemFromOrder({ organizationId: orgId, itemId: item?.id as string }));
  }, []);

  const archiveOldOrders = useCallback(() => {
    if (!orgId) return;

    const oneDayAfter = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    const orderCreatedAt = new Date(order?.createdAt);

    if (orderCreatedAt > oneDayAfter) {
      archiveOrder({
        organizationId: orgId,
        orderId: order?.id,
      });
      deleteLSOrganizationOrder();
    }
  }, [archiveOrder, order?.createdAt, order?.id, orgId]);

  return {
    order,
    orders,
    isIncluded,
    reduceItemQuantity,
    increaseItemQuantity,
    addItems,
    removeItem,
    getOrderInLocalStorage,
    subscribeToOrderByOrganizationId,
    subscribeToOrdersByOrganizationId,
    deleteLSOrganizationOrder,
    archiveOldOrders,
    checkOrderInLocalStorage,
  };
};

export default useOrder;
